import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { GMB } from "../../services/gmbApiConfig";
import { fetchStoreDetails } from "./gmbStoreSlice";

// fetches the GMB verification options  for a store
export const fetchVerificationOptions = createAsyncThunk(
  "gmbVerification/fetchVerificationOptions",
  async (location_id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.VERIFICATION.OPTIONS}`,
        { location_id },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );
      if (response.data?.status === "error") {
        return rejectWithValue(response.data.error_message);
      }
      return response.data.options || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Sends the OTP to the user for verification and returns the verification id
export const sendVerificationOTP = createAsyncThunk(
  "gmbVerification/sendOTP",
  async (
    { account_id, location_id, method, phoneNumber },
    { rejectWithValue },
  ) => {
    try {
      console.log({ account_id, location_id, method, phoneNumber });
      const response = await axios.post(
        `${GMB.ENDPOINTS.VERIFICATION.VERIFY}`,
        {
          account_id,
          location_id,
          method,
          languageCode: "en",
          phoneNumber,
        },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );

      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }

      if (response.data?.status === "error") {
        toast.error("Failed to send OTP");
        return rejectWithValue(response.data.error_message);
      }

      const verificationId =
        response.data?.response?.verification?.name.split("/")[3];
      return { verificationId, success: true };
    } catch (error) {
      return rejectWithValue("Failed to send OTP");
    }
  },
);

// Verifies the OTP and returns the verification status
export const verifyOTP = createAsyncThunk(
  "gmbVerification/verifyOTP",
  async (
    { account_id, location_id, verification_id, pin,storeCode },
    { dispatch,rejectWithValue },
  ) => {
    try { 
      const response = await axios.post(
        `${GMB.ENDPOINTS.VERIFICATION.COMPLETE_VERIFICATION}`,
        {
          account_id,
          location_id,
          verification_id,
          pin,
        },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );

      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }
      if (response.data?.status === "error") {
        toast.error("Failed to verify OTP");
        return rejectWithValue(response.data.error_message || response.data.message);
      }
      console.log("response verifying otp response", response.data);
      //check here if the verification is successful or not and dispatch the action accordingly
      dispatch(fetchStoreDetails(storeCode))
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to verify OTP");
    }
  },
);

const initialState = {
  verificationOptions: null,
  verificationModal: {
    isOpen: false,
    otpSent: false,
    isVerifying: false,
  },
  verificationData: {
    phoneNumber: "",
    otpType: "sms",
    otpCode: "",
    verificationId: null,
  },
  loading: {
    sendOTP: false,
    verifyOTP: false,
    fetchOptions: false,
  },
  error: null,
};

const gmbVerificationSlice = createSlice({
  name: "gmbVerification",
  initialState,
  reducers: {
    setModalOpen: (state, action) => {
      state.verificationModal.isOpen = action.payload;
      if (!action.payload) {
        state.verificationModal.otpSent = false;
        state.verificationData.otpCode = "";
      }
    },
    updateVerificationData: (state, action) => {
      state.verificationData = {
        ...state.verificationData,
        ...action.payload,
      };
    },
    resetVerification: (state) => {
      state.verificationModal = initialState.verificationModal;
      state.verificationData = initialState.verificationData;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Send OTP
      .addCase(sendVerificationOTP.pending, (state) => {
        state.loading.sendOTP = true;
        state.error = null;
      })
      .addCase(sendVerificationOTP.fulfilled, (state, action) => {
        state.loading.sendOTP = false;
        state.verificationModal.otpSent = true;
        state.verificationData.verificationId = action.payload.verificationId;
        if (state.verificationData.otpType === "phone_call") {
          toast.success(
            `Phone call has been initiated to ${state.verificationData.phoneNumber}`,
          );
        } else {
          toast.success(
            `OTP sent successfully to ${state.verificationData.phoneNumber}`,
          );
        }
      })
      .addCase(sendVerificationOTP.rejected, (state, action) => {
        state.loading.sendOTP = false;
        state.error = action.payload;
      })

      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading.verifyOTP = true;
        state.verificationModal.isVerifying = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading.verifyOTP = false;
        state.verificationModal.isVerifying = false;
        state.verificationModal.isOpen = false;
        state.verificationModal.otpSent = false;
        toast.success("Verified successfully!");
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading.verifyOTP = false;
        state.verificationModal.isVerifying = false;
        state.error = action.payload;
      })
      // fetchVerificationOptions
      .addCase(fetchVerificationOptions.pending, (state) => {
        state.loading.fetchOptions = true;
        state.error = null;
      })
      .addCase(fetchVerificationOptions.fulfilled, (state, action) => {
        state.loading.fetchOptions = false;
        state.verificationOptions = action.payload;
      })
      .addCase(fetchVerificationOptions.rejected, (state, action) => {
        state.loading.fetchOptions = false;
        state.error = action.payload;
        toast.error("Failed to fetch verification options");
      });
  },
});

// Actions
export const { setModalOpen, updateVerificationData, resetVerification } =
  gmbVerificationSlice.actions;

// Selectors
export const selectVerificationModal = (state) =>
  state.gmbVerification.verificationModal;
export const selectVerificationData = (state) =>
  state.gmbVerification.verificationData;
export const selectVerificationLoading = (state) =>
  state.gmbVerification.loading;
export const selectVerificationError = (state) => state.gmbVerification.error;
export const selectVerificationOptions = (state) =>
  state.gmbVerification.verificationOptions;

export default gmbVerificationSlice.reducer;
